import React from 'react';
import {
    BarChart, Bar, LineChart, Line, ScatterChart, Scatter,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';

/**
 * ChartRenderer - Displays various chart types from analytical agent
 * 
 * Supports: bar charts, line charts, scatter plots, histograms
 */
const ChartRenderer = ({ chartData }) => {
    if (!chartData || !chartData.type || !chartData.data) {
        return null;
    }

    const { type, title, data } = chartData;

    // Color palette for charts
    const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'];

    const renderChart = () => {
        switch (type) {
            case 'bar':
                // Bar chart for categorical comparisons
                const barData = data.labels?.map((label, idx) => ({
                    name: label,
                    value: data.values[idx]
                })) || [];

                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={barData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="name" stroke="#6b7280" />
                            <YAxis stroke="#6b7280" label={{ value: data.y_label || '', angle: -90, position: 'insideLeft' }} />
                            <Tooltip
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                                labelStyle={{ fontWeight: 'bold', color: '#1f2937' }}
                            />
                            <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                                {barData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                );

            case 'line':
                // Line chart for trends over time
                const lineData = data.x?.map((x, idx) => ({
                    x: x,
                    y: data.y[idx]
                })) || [];

                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={lineData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="x" stroke="#6b7280" label={{ value: data.x_label || '', position: 'insideBottom', offset: -5 }} />
                            <YAxis stroke="#6b7280" label={{ value: data.y_label || '', angle: -90, position: 'insideLeft' }} />
                            <Tooltip
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                            />
                            <Legend />
                            <Line type="monotone" dataKey="y" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 4 }} />
                        </LineChart>
                    </ResponsiveContainer>
                );

            case 'scatter':
                // Scatter plot for correlations
                const scatterData = data.x?.map((x, idx) => ({
                    x: x,
                    y: data.y[idx]
                })) || [];

                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <ScatterChart margin={{ top: 5, right: 30, left: 20, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="x" stroke="#6b7280" label={{ value: data.x_label || '', position: 'insideBottom', offset: -10 }} />
                            <YAxis dataKey="y" stroke="#6b7280" label={{ value: data.y_label || '', angle: -90, position: 'insideLeft' }} />
                            <Tooltip
                                cursor={{ strokeDasharray: '3 3' }}
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                            />
                            <Scatter name={data.y_label || 'Data'} data={scatterData} fill="#3b82f6" />
                        </ScatterChart>
                    </ResponsiveContainer>
                );

            case 'histogram':
                // Histogram for distributions
                const histData = data.bins?.slice(0, -1).map((bin, idx) => ({
                    range: `${bin.toFixed(1)}`,
                    count: data.counts[idx]
                })) || [];

                return (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={histData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis dataKey="range" stroke="#6b7280" label={{ value: data.x_label || '', position: 'insideBottom', offset: -5 }} />
                            <YAxis stroke="#6b7280" label={{ value: data.y_label || 'Frequency', angle: -90, position: 'insideLeft' }} />
                            <Tooltip
                                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                            />
                            <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                );

            default:
                return <div style={{ padding: '20px', color: '#6b7280' }}>Unsupported chart type: {type}</div>;
        }
    };

    return (
        <div className="chart-container" style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '16px',
            marginTop: '12px'
        }}>
            {title && (
                <h4 style={{
                    margin: '0 0 16px 0',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#1f2937'
                }}>
                    {title}
                </h4>
            )}
            {renderChart()}
        </div>
    );
};

export default ChartRenderer;
